import React, { useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { LoginPage } from './pages/LoginPage';

// District Monitoring Pages
import { DEODashboardPage } from './pages/DEODashboardPage';
import { BEODashboardPage } from './pages/BEODashboardPage';
import { SchoolDashboardPage } from './pages/SchoolDashboardPage';
import { MonthlyAssessmentPage } from './pages/MonthlyAssessmentPage';
import { SATMarksPage } from './pages/SATMarksPage';
import { SubmissionStatusPage } from './pages/SubmissionStatusPage';
import { StudentDataPage } from './pages/StudentDataPage';
import { SchoolPerformancePage } from './pages/SchoolPerformancePage';
import { ExecutiveReportPage } from './pages/ExecutiveReportPage';
import { ReportsCenterPage } from './pages/ReportsCenterPage';
import { SystemSettingsPage } from './pages/SystemSettingsPage';
import { ProfilePage } from './pages/ProfilePage';
import { AssessmentConfigPage } from './pages/AssessmentConfigPage';
import { SchoolManagementPage } from './pages/SchoolManagementPage';
import { AttendancePage } from './pages/AttendancePage';

// Additional / Supporting Pages
import { SchoolProfilePage } from './pages/SchoolProfilePage';
import { TeachersManagementPage } from './pages/TeachersManagementPage';
import { BoardClassSetupPage } from './pages/BoardClassSetupPage';
import { AcademicSessionsPage } from './pages/AcademicSessionsPage';
import { AuditLogsPage } from './pages/AuditLogsPage';
import { DataImportCenterPage } from './pages/DataImportCenterPage';
import { HistoricalDataPage } from './pages/HistoricalDataPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { StudentLifecycleManagementPage } from './pages/StudentLifecycleManagementPage';
import { InterventionManagementPage } from './pages/InterventionManagementPage';
import { BoardExamPrepPage } from './pages/BoardExamPrepPage';
import { RealPYQRepositoryPage } from './pages/RealPYQRepositoryPage';
import { PYQIntelligencePage } from './pages/PYQIntelligencePage';
import { PYQCorpusManagerPage } from './pages/PYQCorpusManagerPage';
import { MathSolutionPage } from './pages/MathSolutionPage';
import { FutureModulePage } from './pages/FutureModulePage';
import { DailyAttendanceMonitoringPage } from './pages/DailyAttendanceMonitoringPage';

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [currentPath, setCurrentPath] = useState<string>(() => window.location.pathname);

  // Direct standalone route listener for /daily-attendance
  React.useEffect(() => {
    const onPopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Direct URL access to https://nuh-education-portal.pages.dev/daily-attendance
  if (currentPath === '/daily-attendance' || currentPath.startsWith('/daily-attendance/')) {
    return (
      <DailyAttendanceMonitoringPage
        onBackToPortal={() => {
          window.history.pushState(null, '', '/');
          setCurrentPath('/');
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1E38] flex flex-col items-center justify-center text-white">
        <div className="w-10 h-10 border-3 border-amber-400 border-t-transparent rounded-full animate-spin mb-3" />
        <div className="text-sm font-bold text-amber-400">Loading Official Portal...</div>
        <div className="text-xs text-slate-300 mt-1">District Education Office, Nuh • Department of School Education, Haryana</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const isDEO = user.role === 'deo' || user.role === 'admin';
  const isBEO = user.role === 'beo';

  // Render role-specific navigation views
  const renderContent = () => {
    // 1. DEO (District Administration) Views
    if (isDEO) {
      switch (activeTab) {
        case 'dashboard':
        case 'district-overview':
        case 'block-performance':
        case 'subject-analysis':
        case 'monthly-trends':
          return <DEODashboardPage onNavigate={setActiveTab} />;
        case 'school-management':
        case 'schools-management':
          return <SchoolManagementPage />;
        case 'school-performance':
        case 'low-performing-schools':
          return <SchoolPerformancePage />;
        case 'assessment-monitoring':
        case 'submission-status':
        case 'pending-submissions':
          return <SubmissionStatusPage />;
        case 'sat-monitoring':
        case 'sat-marks':
          return <SATMarksPage />;
        case 'assessment-config':
        case 'assessment-configuration':
        case 'max-marks-config':
          return <AssessmentConfigPage />;
        case 'student-analysis':
        case 'student-data':
        case 'students':
          return <StudentDataPage />;
        case 'student-lifecycle':
        case 'student-management':
        case 'student-status':
          return <StudentLifecycleManagementPage />;
        case 'monthly-assessment':
          return <MonthlyAssessmentPage />;
        case 'reports':
        case 'export':
          return <ReportsCenterPage />;
        case 'executive-report':
          return <ExecutiveReportPage />;
        case 'historical-data':
        case 'historical':
          return <HistoricalDataPage />;
        case 'data-import':
        case 'import':
          return <DataImportCenterPage />;
        case 'user-management':
        case 'users':
          return <UserManagementPage />;
        case 'settings':
          return <SystemSettingsPage />;
        case 'profile':
          return <ProfilePage />;
        // Supporting tabs
        case 'daily-attendance':
          return <DailyAttendanceMonitoringPage onBackToPortal={() => setActiveTab('dashboard')} />;
        case 'attendance':
          return <AttendancePage />;
        case 'interventions':
          return <InterventionManagementPage />;
        case 'teachers':
          return <TeachersManagementPage />;
        case 'school-profile':
          return <SchoolProfilePage />;
        case 'academic-sessions':
          return <AcademicSessionsPage />;
        case 'board-class-setup':
          return <BoardClassSetupPage />;
        case 'audit-logs':
          return <AuditLogsPage />;
        case 'pyq-intelligence':
          return <PYQIntelligencePage />;
        case 'pyq-corpus':
          return <PYQCorpusManagerPage />;
        case 'math-solutions':
          return <MathSolutionPage />;
        case 'real-pyq-repo':
          return <RealPYQRepositoryPage />;
        default:
          return <DEODashboardPage onNavigate={setActiveTab} />;
      }
    }

    // 2. BEO (Block Administration) Views
    if (isBEO) {
      switch (activeTab) {
        case 'dashboard':
        case 'block-analysis':
          return <BEODashboardPage onNavigate={setActiveTab} />;
        case 'daily-attendance':
          return <DailyAttendanceMonitoringPage onBackToPortal={() => setActiveTab('dashboard')} />;
        case 'schools':
        case 'school-performance':
        case 'low-performing-schools':
          return <SchoolPerformancePage />;
        case 'historical-data':
        case 'historical':
          return <HistoricalDataPage />;
        case 'student-lifecycle':
        case 'student-management':
        case 'student-status':
          return <StudentLifecycleManagementPage />;
        case 'assessment-monitoring':
        case 'pending-submissions':
        case 'submission-status':
          return <SubmissionStatusPage />;
        case 'sat-monitoring':
        case 'sat-marks':
          return <SATMarksPage />;
        case 'student-data':
        case 'students':
          return <StudentDataPage />;
        case 'monthly-assessment':
          return <MonthlyAssessmentPage />;
        case 'reports':
        case 'export':
          return <ReportsCenterPage />;
        case 'profile':
          return <ProfilePage />;
        default:
          return <BEODashboardPage onNavigate={setActiveTab} />;
      }
    }

    // 3. SCHOOL (Head / Principal / Teacher) Views
    switch (activeTab) {
      case 'dashboard':
        return <SchoolDashboardPage onNavigate={setActiveTab} />;
      case 'daily-attendance':
        return <DailyAttendanceMonitoringPage onBackToPortal={() => setActiveTab('dashboard')} />;
      case 'student-lifecycle':
      case 'student-management':
      case 'student-status':
        return <StudentLifecycleManagementPage />;
      case 'student-data':
      case 'students':
        return <StudentDataPage />;
      case 'monthly-assessment':
      case 'marks-entry':
        return <MonthlyAssessmentPage />;
      case 'sat-marks':
        return <SATMarksPage />;
      case 'submission-status':
        return <SubmissionStatusPage />;
      case 'school-performance':
        return <SchoolPerformancePage />;
      case 'reports':
        return <ReportsCenterPage />;
      case 'profile':
      case 'principal-profile':
      case 'my-profile':
        return <ProfilePage />;
      case 'school-profile':
        return <SchoolProfilePage />;
      case 'attendance':
        return <AttendancePage />;
      case 'interventions':
        return <InterventionManagementPage />;
      case 'board-exam-prep':
        return <BoardExamPrepPage />;
      case 'pyq-intelligence':
        return <PYQIntelligencePage />;
      case 'pyq-corpus':
        return <PYQCorpusManagerPage />;
      case 'math-solutions':
        return <MathSolutionPage />;
      case 'real-pyq-repo':
        return <RealPYQRepositoryPage />;
      default:
        return <SchoolDashboardPage />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F6F9]">
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          activeTab={activeTab}
          setActiveTab={(tab) => {
            setActiveTab(tab);
            setSidebarOpen(false);
          }}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <main className="flex-1 overflow-y-auto p-3.5 sm:p-5 lg:p-6">
          <div className="max-w-7xl mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Uncaught Rendering Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0B1E38] text-white flex flex-col items-center justify-center p-6">
          <div className="max-w-md w-full bg-white text-slate-900 border border-slate-300 rounded-lg p-6 shadow-xl text-center">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-700 mx-auto flex items-center justify-center mb-3 text-xl font-bold">
              !
            </div>
            <h2 className="text-base font-bold text-slate-900 mb-1">Portal Interface Notice</h2>
            <p className="text-xs text-slate-600 mb-4">
              {this.state.error?.message || 'An unexpected rendering issue occurred.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="bg-[#0B1E38] hover:bg-[#15345e] text-white font-bold text-xs px-4 py-2 rounded transition"
            >
              Reload Portal
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
};

export default App;
